import axios from "axios";
import API_ENDPOINTS from "../config/api.js";
import logger from "../utils/logger.js";

// Configuration axios avec intercepteur pour les tokens
const apiClient = axios.create({
  baseURL: API_ENDPOINTS.BASE,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Intercepteur pour ajouter le token d'authentification
apiClient.interceptors.request.use(
  (config) => {
    const startTime = Date.now();
    config.metadata = { startTime };
    
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    logger.debug('Requête API envoyée', {
      method: config.method?.toUpperCase(),
      url: config.url,
      headers: config.headers,
      data: config.data
    });
    
    return config;
  },
  (error) => {
    logger.error('Erreur lors de l\'envoi de la requête', {
      error: error.message,
      config: error.config
    });
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs d'authentification
apiClient.interceptors.response.use(
  (response) => {
    const duration = Date.now() - response.config.metadata.startTime;
    
    logger.api(
      response.config.method?.toUpperCase(),
      response.config.url,
      response.config.data,
      response.data,
      response.status,
      duration
    );
    
    return response;
  },
  (error) => {
    const duration = error.config?.metadata ? Date.now() - error.config.metadata.startTime : null;
    
    logger.api(
      error.config?.method?.toUpperCase(),
      error.config?.url,
      error.config?.data,
      error.response?.data,
      error.response?.status,
      duration
    );
    
    if (error.response?.status === 401) {
      logger.warn('Token d\'authentification expiré, redirection vers la page de connexion');
      localStorage.removeItem("token");
      window.location.href = "/connexion";
    }
    
    return Promise.reject(error);
  }
);

// Service pour les informations de l'entreprise
export const companyService = {
  getInfo: async () => {
    return apiClient.get(API_ENDPOINTS.COMPANY_INFO);
  },
  updateInfo: (id, data) => {
    return apiClient.put(`${API_ENDPOINTS.COMPANY_INFO}/update/${id}`, data);
  },
  updateLogo: (id, logo) => {
    return apiClient.patch(`${API_ENDPOINTS.COMPANY_INFO}/logo/${id}`, { logo: logo ?? "" });
  },
  uploadLogo: (formData) => {
    return apiClient.post(API_ENDPOINTS.COMPANY_LOGO, formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
  },
  uploadStamp: (formData) => {
    return apiClient.post(API_ENDPOINTS.COMPANY_STAMP, formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
  },
  updateStamp: (id, stamp) => {
    return apiClient.patch(API_ENDPOINTS.COMPANY_STAMP_UPDATE(id), { stamp });
  },
  fetchLogo: (url) => apiClient.post(API_ENDPOINTS.UPLOAD_FETCH_LOGO, { url }),
  add: (data) => {
    invalidateCache('company');
    return apiClient.post(`${API_ENDPOINTS.COMPANY_INFO}/add`, data);
  },
};

// Service pour les transporteurs
export const transporteurService = {
  getAll: async () => {
    const response = await apiClient.get(API_ENDPOINTS.TRANSPORTEURS);
    // Gérer le format de réponse des contrôleurs CRUD
    return { data: response.data.data || response.data };
  },
  add: (data) => {
    return apiClient.post(`${API_ENDPOINTS.TRANSPORTEURS}/add`, data);
  },
  update: (id, data) => {
    return apiClient.put(`${API_ENDPOINTS.TRANSPORTEURS}/update/${id}`, data);
  },
  delete: (id) => {
    return apiClient.delete(API_ENDPOINTS.TRANSPORTEUR_DELETE(id));
  },
};

// Service pour les clients (anciennement transporteurs)
export const clientService = {
  getAll: async () => {
    const response = await apiClient.get(API_ENDPOINTS.CLIENTS);
    // Gérer le format de réponse des contrôleurs CRUD
    return { data: response.data.data || response.data };
  },
  add: (data) => {
    return apiClient.post(`${API_ENDPOINTS.CLIENTS}/add`, data);
  },
  update: (id, data) => {
    return apiClient.put(`${API_ENDPOINTS.CLIENTS}/update/${id}`, data);
  },
  delete: (id) => {
    return apiClient.delete(API_ENDPOINTS.CLIENT_DELETE(id));
  },
};

// Service pour les clients
export const clientListService = {
  getAll: async () => {
    const response = await apiClient.get(API_ENDPOINTS.LISTE_CLIENTS);
    // Gérer le format de réponse des contrôleurs CRUD
    return { data: response.data.data || response.data };
  },
  add: (data) => {
    return apiClient.post(`${API_ENDPOINTS.LISTE_CLIENTS}/add`, data);
  },
  update: (id, data) => {
    return apiClient.put(`${API_ENDPOINTS.LISTE_CLIENTS}/update/${id}`, data);
  },
  delete: (id) => {
    return apiClient.delete(API_ENDPOINTS.LISTE_CLIENT_DELETE(id));
  },
};

// Service pour les commissionnaires
export const commissionnaireService = {
  getAll: async () => {
    const response = await apiClient.get(API_ENDPOINTS.COMMISSIONNAIRES);
    // Gérer le format de réponse des contrôleurs CRUD
    return { data: response.data.data || response.data };
  },
  add: (data) => {
    return apiClient.post(`${API_ENDPOINTS.COMMISSIONNAIRES}/add`, data);
  },
  update: (id, data) => {
    return apiClient.put(`${API_ENDPOINTS.COMMISSIONNAIRES}/update/${id}`, data);
  },
  delete: (id) => {
    return apiClient.delete(`${API_ENDPOINTS.COMMISSIONNAIRES}/delete/${id}`);
  },
};

// Service pour les devis
export const estimateService = {
  getAll: async () => {
    return apiClient.get(API_ENDPOINTS.ESTIMATES);
  },
  getById: async (id) => {
    return apiClient.get(`${API_ENDPOINTS.ESTIMATES}/${id}`);
  },
  add: (data) => {
    return apiClient.post(`${API_ENDPOINTS.ESTIMATES}/add`, data);
  },
  update: (id, data) => {
    return apiClient.put(`${API_ENDPOINTS.ESTIMATES}/update/${id}`, data);
  },
  delete: (id) => {
    return apiClient.delete(API_ENDPOINTS.ESTIMATE_DELETE(id));
  },
};

// Service pour les factures
export const invoiceService = {
  getAll: async () => {
    return apiClient.get(API_ENDPOINTS.INVOICES);
  },
  add: (data) => {
    return apiClient.post(`${API_ENDPOINTS.INVOICES}/add`, data);
  },
  update: (id, data) => {
    return apiClient.put(`${API_ENDPOINTS.INVOICES}/update/${id}`, data);
  },
  delete: (id) => {
    return apiClient.delete(`${API_ENDPOINTS.INVOICES}/delete/${id}`);
  },
};

// Service pour les ordres de transport
export const transportOrderService = {
  getAll: async () => {
    return apiClient.get(API_ENDPOINTS.TRANSPORT_ORDERS);
  },
  add: (data) => {
    return apiClient.post(API_ENDPOINTS.TRANSPORT_ORDER_ADD, data);
  },
  update: (id, data) => {
    return apiClient.put(API_ENDPOINTS.TRANSPORT_ORDER_UPDATE(id), data);
  },
  delete: (id) => {
    return apiClient.delete(API_ENDPOINTS.TRANSPORT_ORDER_DELETE(id));
  },
};

// Service pour les conditions de transport
export const transportConditionsService = {
  getAll: async () => {
    return apiClient.get(API_ENDPOINTS.TRANSPORT_CONDITIONS);
  },
  add: (data) => {
    return apiClient.post(API_ENDPOINTS.TRANSPORT_CONDITIONS_ADD, data);
  },
  update: (id, data) => {
    return apiClient.put(API_ENDPOINTS.TRANSPORT_CONDITIONS_UPDATE(id), data);
  },
  delete: (id) => {
    return apiClient.delete(API_ENDPOINTS.TRANSPORT_CONDITIONS_DELETE(id));
  },
};

// Service pour les messages
export const messageService = {
  getAll: async () => {
    return apiClient.get(API_ENDPOINTS.MESSAGES);
  },
  add: (data) => {
    return apiClient.post(API_ENDPOINTS.MESSAGE_ADD, data);
  },
  delete: (id) => {
    return apiClient.delete(API_ENDPOINTS.MESSAGE_DELETE(id));
  },
};

// Service pour les notes
export const notesService = {
  get: (userId) => apiClient.get(API_ENDPOINTS.NOTES(userId)),
  save: (userId, notes) => apiClient.post(API_ENDPOINTS.NOTES(userId), { notes }),
};

// Service pour l'authentification
export const authService = {
  login: (credentials) => apiClient.post(API_ENDPOINTS.LOGIN, credentials),
};

// Service pour les utilisateurs
export const userService = {
  getAll: async () => {
    return apiClient.get(API_ENDPOINTS.USERS);
  },
  add: (data) => {
    return apiClient.post(`${API_ENDPOINTS.USERS}/add`, data);
  },
  update: (id, data) => {
    return apiClient.put(`${API_ENDPOINTS.USERS}/update/${id}`, data);
  },
  delete: (id) => {
    return apiClient.delete(`${API_ENDPOINTS.USERS}/delete/${id}`);
  },
};

// Fonction utilitaire pour construire les URLs des images
export const buildImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith("http")) return imagePath;
  return `${API_ENDPOINTS.BASE}${imagePath}`;
};

// Fonction pour nettoyer le cache (maintenant vide car pas de cache)
export const clearCache = () => {
  // Cache supprimé - fonction vide pour compatibilité
};

// Fonction pour obtenir les statistiques du cache (maintenant vide)
export const getCacheStats = () => {
  return {
    size: 0,
    entries: []
  };
};

// Export des fonctions existantes pour compatibilité
export const getCompanyInfo = companyService.getInfo;
export const getClients = clientService.getAll;
export const getCommissionnaires = commissionnaireService.getAll;
export const getAllEstimates = estimateService.getAll;
export const getAllFactures = invoiceService.getAll;
export const addEstimate = estimateService.add;
export const addFacture = invoiceService.add;
export const updateEstimate = estimateService.update;
export const updateFacture = invoiceService.update;
export const apiDeleteEstimate = estimateService.delete;
export const apiDeleteFacture = invoiceService.delete;
export const addClients = clientService.add;
export const updateClients = clientService.update;
export const apiDeleteClients = clientService.delete;
export const addCommissionnaire = commissionnaireService.add;
export const updateCommissionnaire = commissionnaireService.update;
export const deleteCommissionnaire = commissionnaireService.delete;
export const updateCompanyLogo = companyService.updateLogo;

// Fonction pour les appels sécurisés (compatibilité)
export async function secureFetch(url, options = {}) {
  const token = localStorage.getItem('token');
  const headers = {
    ...options.headers,
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
  const res = await fetch(url, { ...options, headers });
  if (res.status === 401) {
    window.location.href = '/connexion';
    return;
  }
  return res.json();
}

export default apiClient; 